/**
 * Reverse mappers for the Monthly Challenge Details page (FE-2324): translate a
 * campaign + its challenge groups + tiered tasks (fetched from the reused
 * season endpoints with `campaign_id`) back into the UI shapes the Details view
 * renders — `MonthlyDetailsData` (General header + mode) and the wizard's
 * `MonthlyChallengeDraft[]`.
 *
 * These are the inverse of the create-flow forward mappers
 * (`monthlyCampaignMappers` / `monthlyTaskMapper`): task type is inferred from
 * `data_source` + `rules`, tiers are re-hydrated from the nested `tiers` array,
 * and the type-specific fields (pick type, odds, sports/leagues, casino
 * provider/game, action, currency) are read back off the same rule vocabulary
 * the create request emitted.
 *
 * Contract confirmed (FE-2324, 2026-07-24) against live test03 responses:
 * `GET /gamepass/v2/admin/challenges?campaign_id=…` returns challenge groups
 * shaped like `ChallengeApiRecord`, and `GET …/tasks?campaign_id=…` returns
 * tasks shaped like `CampaignTaskListRecord` — every field this mapper reads
 * (`challenge_id`, `data_source`, `bet_type`, `min_odds_american`,
 * `outcome_required`, `min_bet_amount`, `rules`, and the nested `tiers`
 * `{ threshold, rewards: [{ amount, currency }], xp_reward }`) is present with
 * matching key names. The currency→reward-type round-trip is many-to-one (both
 * play currencies collapse to Rebet Coins, mirroring the Season reverse
 * mapper); the standalone per-tier `xp_reward` re-hydrates into the tier's
 * `xpReward` input (FE-2427).
 */
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import {
  DEFAULT_MONTHLY_ACTION,
  DEFAULT_MONTHLY_CASINO_ACTION,
  DEFAULT_MONTHLY_PICK_TYPE,
  DEFAULT_MONTHLY_TASK_TYPE,
  DEFAULT_TIER_REWARD_TYPE,
  parseUtcHour,
  usesSportsbookPickFields,
  type MonthlyChallengeDraft,
  type MonthlyPickType,
  type MonthlyPlayWinAction,
  type MonthlyTaskType,
  type MonthlyTierDraft,
} from "../../components/GamePass/Monthly/monthlyChallengeData";
import type { MonthlyDetailsData } from "../../components/GamePass/Monthly/Details/monthlyDetailsData";
import { MONEY_TYPE_TO_BET_CURRENCY } from "../../components/GamePass/CreateSeason/createSeasonData";
import { CHALLENGE_CURRENCY_TO_REWARD_TYPE } from "./challengeRewards";
import { buildCasinoGameLabelsFromTaskGames } from "./casinoGameNormalizers";
import { mapApiCampaignToSeasonData } from "./campaignsMappers";
import {
  CAMPAIGN_API_STATUS,
  TASK_CASINO_ACTION,
  TASK_DATA_SOURCE,
  TASK_EVENT_TYPE,
  TASK_OUTCOME_REQUIRED,
  TASK_REBETS_OUTCOME,
  TASK_RULE_FIELD,
} from "../../types/gamePass";
import type {
  CampaignApiRecord,
  CampaignTaskListRecord,
  CampaignTierReward,
  ChallengeApiRecord,
  TaskBetType,
  TaskRule,
  TaskRuleField,
} from "../../types/gamePass";

dayjs.extend(utc);

/**
 * Full date+time range for the Details header, e.g.
 * "August 1, 2026 (12:00am) - August 31, 2026 (11:59pm)". Stored UTC is shown
 * in the admin's local zone so it matches the wall-clock the Schedule pickers
 * display (mirrors the Season Details `formatFullDateTimeRange`). Unlike the
 * listing card's compact `MMM D` range, the Details view surfaces the exact
 * time. Returns `null` when either boundary is missing / unparseable so the
 * caller can fall back to the card range.
 */
const formatMonthlyFullDateTimeRange = (
  start?: string | null,
  end?: string | null,
): string | null => {
  const startDay = start ? dayjs.utc(start).local() : null;
  const endDay = end ? dayjs.utc(end).local() : null;
  if (!startDay?.isValid() || !endDay?.isValid()) return null;
  return `${startDay.format("MMMM D, YYYY (h:mma)")} - ${endDay.format(
    "MMMM D, YYYY (h:mma)",
  )}`;
};

/** BE `bet_type` → Monthly Pick Type pill. Absent / SGP (no monthly SGP pill)
 *  falls back to "Any". Inverse of `PICK_TYPE_TO_BET_TYPE`. */
const BET_TYPE_TO_PICK_TYPE: Record<TaskBetType, MonthlyPickType> = {
  SINGLE: "SINGLE",
  PARLAY: "PARLAY",
  LIVE: "LIVE",
  SGP: "ANY",
};

/** BE `outcome_required` → Sportsbook Play/Win action. Anything other than an
 *  explicit WIN reads as "Play" (the widest net). */
const outcomeToSportsbookAction = (
  value: string | null | undefined,
): MonthlyPlayWinAction =>
  value === TASK_OUTCOME_REQUIRED.WIN ? "WIN" : "PLAY";

/** Casino `outcome` rule value → Play/Win action ("settled" → Play). */
const outcomeRuleToCasinoAction = (
  value: string | undefined,
): MonthlyPlayWinAction => (value === TASK_CASINO_ACTION.WIN ? "WIN" : "PLAY");

/** Collect a rule's value(s) as a flat string list: `eq` yields one entry,
 *  `in` yields the array. Non-string scalars are coerced; missing / boolean
 *  rules yield `[]`. */
const readRuleValues = (
  rules: ReadonlyArray<TaskRule> | null | undefined,
  field: TaskRuleField,
): string[] => {
  if (!Array.isArray(rules)) return [];
  const values: string[] = [];
  for (const rule of rules) {
    if (rule.field !== field) continue;
    const { value } = rule;
    if (Array.isArray(value)) {
      values.push(...value.map(String));
    } else if (typeof value === "string" || typeof value === "number") {
      values.push(String(value));
    }
  }
  return values;
};

const hasRule = (
  rules: ReadonlyArray<TaskRule> | null | undefined,
  field: TaskRuleField,
): boolean => Array.isArray(rules) && rules.some((rule) => rule.field === field);

const firstRuleValue = (
  rules: ReadonlyArray<TaskRule> | null | undefined,
  field: TaskRuleField,
): string | undefined => readRuleValues(rules, field)[0];

/** Play/Win action for a Rebets task — read off the RULES, not the
 *  `outcome_required` column: the BE ignores that column on a hand-authored
 *  `rules[]` task and even defaults it to `WIN` on create, so a placement-mode
 *  ("Play") task can come back with a stored `WIN` column. Priority:
 *  `count_on_placement` → Play (the two are mutually exclusive on the wire);
 *  an `outcome: "win"` rule → Win; else the column for legacy `is_rebet`
 *  tasks, which predate the rule vocabulary. */
const rebetsRuleToSportsbookAction = (
  task: CampaignTaskListRecord,
): MonthlyPlayWinAction => {
  if (hasRule(task.rules, TASK_RULE_FIELD.COUNT_ON_PLACEMENT)) return "PLAY";
  const outcomeRule = firstRuleValue(task.rules, TASK_RULE_FIELD.OUTCOME);
  if (outcomeRule?.toLowerCase() === TASK_REBETS_OUTCOME.WIN) return "WIN";
  return outcomeToSportsbookAction(task.outcome_required);
};

/** Infer the Monthly task type from the wire `data_source` + `rules`, inverting
 *  `mapMonthlyChallengeToTaskRequest`. SOCIAL splits on the milestone rule.
 *
 *  SPORTSBOOK covers two task types. Since FE-2441 a Rebets task carries the
 *  same trigger columns as a Sportsbook Pick, so the marker rule the create
 *  mapper stamps on every Rebets task is the primary discriminator —
 *  `rebets_received` since FE-2535 (the only authored marker), with the
 *  retired `is_rebet` still recognized on tasks saved before then.
 *  Tasks saved before FE-2441 have no marker at all; for those the legacy
 *  inference applies — whether the task carries a genuine pick qualifier (a
 *  plain COUNT with none is the old REBETS interim shape).
 *
 *  The legacy discriminator is deliberately narrow: a pre-FE-2441 REBETS task
 *  was authored with no pick fields, but the BE echoes non-authored columns
 *  back with defaults (`outcome_required: "ANY_SETTLED"`, `min_bet_amount: 0`,
 *  a defaulted `bet_type`), so keying REBETS-vs-Picks off those would mis-read
 *  every saved REBETS challenge as Sportsbook Picks on the next read. The
 *  fields that only ever appear when the admin actually configured a
 *  Sportsbook Pick are a *positive* min bet, a *non-zero* min odds, and the
 *  sport / league / wager currency rules — those (and only those) mark an
 *  unmarked task as SPORTSBOOK_PICKS. A genuine Sportsbook Pick always carries
 *  a positive min bet + a money_type rule (both required by the card's
 *  validation), so it is never misread as REBETS. */
const isPositiveNumber = (value: number | null | undefined): boolean =>
  typeof value === "number" && value > 0;

const isNonZeroNumber = (value: number | null | undefined): boolean =>
  typeof value === "number" && value !== 0;

const inferTaskType = (task: CampaignTaskListRecord): MonthlyTaskType => {
  const dataSource = task.data_source;
  if (dataSource === TASK_DATA_SOURCE.SOCIAL) {
    if (hasRule(task.rules, TASK_RULE_FIELD.REFERRED_USER_MADE_QUALIFYING_DEPOSIT)) {
      return "REFERRAL_BONUS";
    }
    // Login Streak: authored as `event_type: daily_login` + empty `rules`.
    // Legacy tasks may still carry a deprecated `consecutive_days` rule
    // (without Seasons' pick-streak `shared_device_flag`).
    if (
      task.event_type === TASK_EVENT_TYPE.DAILY_LOGIN ||
      (hasRule(task.rules, TASK_RULE_FIELD.CONSECUTIVE_DAYS) &&
        !hasRule(task.rules, TASK_RULE_FIELD.SHARED_DEVICE_FLAG))
    ) {
      return "LOGIN_STREAK";
    }
    return "FOLLOW_FOR_FOLLOW";
  }
  if (dataSource === TASK_DATA_SOURCE.CASINO) {
    return "CASINO_PICKS";
  }
  if (dataSource === TASK_DATA_SOURCE.SPORTSBOOK) {
    // Either marker identifies a Rebets task: `rebets_received` (the only
    // authored marker since FE-2535 — the pick owner is credited) or the
    // retired `is_rebet` on legacy saved tasks, kept readable so they still
    // open as Rebets and upgrade to `rebets_received` on their next save.
    if (
      hasRule(task.rules, TASK_RULE_FIELD.IS_REBET) ||
      hasRule(task.rules, TASK_RULE_FIELD.REBETS_RECEIVED)
    ) {
      return "REBETS";
    }
    const hasPickQualifier =
      isPositiveNumber(task.min_bet_amount) ||
      isNonZeroNumber(task.min_odds_american) ||
      hasRule(task.rules, TASK_RULE_FIELD.SPORT) ||
      hasRule(task.rules, TASK_RULE_FIELD.LEAGUE_ID) ||
      hasRule(task.rules, TASK_RULE_FIELD.MONEY_TYPE);
    return hasPickQualifier ? "SPORTSBOOK_PICKS" : "REBETS";
  }
  return DEFAULT_MONTHLY_TASK_TYPE;
};

/** Re-hydrate a persisted tier reward back into the editor's select value.
 *  `play` / `value` collapse to Rebet Coins / Rebet Cash (same as Seasons).
 *  Bonus currencies (`free_pick`, `profit_boost`, `deposit_match`) stay as the
 *  select value so the dropdown can re-select Deposit Match / Free Pick /
 *  Profit Boost. */
const mapPersistedTierReward = (
  reward?: CampaignTierReward,
): Pick<
  MonthlyTierDraft,
  "rewardType" | "bonusRewardType" | "bonusCurrency" | "params"
> => {
  if (!reward?.currency) {
    return { rewardType: DEFAULT_TIER_REWARD_TYPE };
  }
  const params =
    reward.params && typeof reward.params === "object"
      ? Object.fromEntries(
          Object.entries(reward.params).map(([name, value]) => [
            name,
            String(value),
          ]),
        )
      : undefined;
  if (reward.currency === "play" || reward.currency === "value") {
    return {
      rewardType: CHALLENGE_CURRENCY_TO_REWARD_TYPE[reward.currency],
      ...(params ? { params } : {}),
    };
  }
  return {
    rewardType: reward.currency,
    bonusRewardType: reward.reward_type ?? reward.currency,
    bonusCurrency: reward.currency,
    ...(params ? { params } : {}),
  };
};

/** Re-hydrate the escalating tier rows from the task's `tiers` array. Stable
 *  ids are derived from the challenge id + position (the wire carries no tier
 *  id). Falls back to a single blank tier so a card always renders at least one
 *  row, matching the wizard's invariant. */
const mapTiersToDrafts = (
  challengeId: string,
  tiers: CampaignTaskListRecord["tiers"],
): MonthlyTierDraft[] => {
  if (!tiers || tiers.length === 0) {
    return [
      {
        id: `${challengeId}-tier-0`,
        threshold: "",
        rewardAmount: "",
        rewardType: DEFAULT_TIER_REWARD_TYPE,
        xpReward: "",
      },
    ];
  }
  return tiers.map((tier, index) => {
    const reward = tier.rewards?.[0];
    return {
      id: `${challengeId}-tier-${index}`,
      threshold:
        typeof tier.threshold === "number" ? String(tier.threshold) : "",
      rewardAmount:
        typeof reward?.amount === "number" ? String(reward.amount) : "",
      ...mapPersistedTierReward(reward),
      // A `0` / absent XP bonus re-hydrates as a blank input so the round-trip
      // (blank → 0 → blank) is stable and the field reads as "no XP set".
      xpReward:
        typeof tier.xp_reward === "number" && tier.xp_reward > 0
          ? String(tier.xp_reward)
          : "",
    };
  });
};

/** Group a campaign's tasks under their parent challenge — by `challenge_id`
 *  when present, else by the `challenge` display name (mirrors the Season
 *  Details grouping fallback for tasks created before the group persisted). */
const selectTasksForChallenge = (
  challenge: ChallengeApiRecord,
  tasks: ReadonlyArray<CampaignTaskListRecord>,
): CampaignTaskListRecord[] => {
  const challengeId = challenge.id ?? challenge.challenge_id;
  return tasks.filter((task) => {
    if (task.challenge_id) return task.challenge_id === challengeId;
    return task.challenge === challenge.name;
  });
};

/** Map one challenge group + its (single, in the monthly model) task into a
 *  `MonthlyChallengeDraft`. Every card comes back saved + collapsed so the
 *  Details view opens on the read-only summaries. */
const mapChallengeToDraft = (
  challenge: ChallengeApiRecord,
  tasks: ReadonlyArray<CampaignTaskListRecord>,
  campaignStreakResetHour?: number | string | null,
): MonthlyChallengeDraft => {
  const challengeId =
    challenge.id ?? challenge.challenge_id ?? `challenge-${challenge.name}`;
  const task = selectTasksForChallenge(challenge, tasks)[0];

  // Defensive: the confirmed task GET shape always carries a `tiers` array. A
  // task without one is a malformed record — surface it (dev console) so the
  // card falling back to a single blank tier is a visible signal, not silent
  // data loss. Cheap to silence via the global logger if it ever gets noisy.
  if (task && !Array.isArray(task.tiers)) {
    // eslint-disable-next-line no-console
    console.warn(
      "[monthlyDetailsMapper] Campaign task has no `tiers` array (malformed record); rendering a single blank tier.",
      { challengeId, taskId: task.id },
    );
  }
  const taskType = task ? inferTaskType(task) : DEFAULT_MONTHLY_TASK_TYPE;
  const wagerCurrencyRule = task
    ? firstRuleValue(task.rules, TASK_RULE_FIELD.MONEY_TYPE)
    : undefined;
  const wagerCurrency = wagerCurrencyRule
    ? MONEY_TYPE_TO_BET_CURRENCY[wagerCurrencyRule]
    : undefined;

  const base: MonthlyChallengeDraft = {
    id: challengeId,
    name: challenge.name ?? task?.name ?? "",
    description: challenge.description ?? task?.description ?? "",
    taskType,
    pickType: DEFAULT_MONTHLY_PICK_TYPE,
    minimumOdds: "",
    sportsbookAction: DEFAULT_MONTHLY_ACTION,
    sports: [],
    leagues: [],
    sportsbookMinBet: "",
    casinoProviders: [],
    casinoGames: [],
    casinoGameLabels: {},
    casinoAction: DEFAULT_MONTHLY_CASINO_ACTION,
    casinoMinBet: "",
    wagerCurrency: undefined,
    streakResetHour: "",
    tiers: mapTiersToDrafts(challengeId, task?.tiers),
    isExpanded: false,
    isSaved: true,
    // Real backend ids (no name fallback) so an in-place edit can PUT the exact
    // challenge group + task. Their presence marks the card as persisted.
    serverChallengeId: challenge.id ?? challenge.challenge_id ?? undefined,
    serverTaskId: task?.id ?? undefined,
  };

  if (!task) return base;

  // Rebets shares the full sportsbook trigger vocabulary since FE-2441, so
  // both hydrate the same fields (the marker rules only feed `inferTaskType`).
  // The Play/Win pill diverges: Sportsbook Picks round-trips off
  // `outcome_required`, but a Rebets task reads its rules — the BE ignores the
  // column on rules[] tasks (FE-2535), see `rebetsRuleToSportsbookAction`.
  if (usesSportsbookPickFields(taskType)) {
    return {
      ...base,
      pickType: task.bet_type
        ? (BET_TYPE_TO_PICK_TYPE[task.bet_type as TaskBetType] ??
          DEFAULT_MONTHLY_PICK_TYPE)
        : DEFAULT_MONTHLY_PICK_TYPE,
      // `0` means "not authored" on both floors (the create mapper sends
      // `null`, but the BE echoes defaulted `0` columns on e.g. legacy REBETS
      // tasks) — hydrate it as blank so the editor doesn't prefill an invalid
      // value.
      minimumOdds:
        typeof task.min_odds_american === "number" &&
        task.min_odds_american !== 0
          ? String(task.min_odds_american)
          : "",
      sportsbookAction:
        taskType === "REBETS"
          ? rebetsRuleToSportsbookAction(task)
          : outcomeToSportsbookAction(task.outcome_required),
      sports: readRuleValues(task.rules, TASK_RULE_FIELD.SPORT),
      leagues: readRuleValues(task.rules, TASK_RULE_FIELD.LEAGUE_ID),
      sportsbookMinBet:
        typeof task.min_bet_amount === "number" && task.min_bet_amount > 0
          ? String(task.min_bet_amount)
          : "",
      wagerCurrency,
    };
  }

  if (taskType === "CASINO_PICKS") {
    return {
      ...base,
      casinoProviders: readRuleValues(task.rules, TASK_RULE_FIELD.PROVIDER),
      casinoGames: readRuleValues(task.rules, TASK_RULE_FIELD.GAME),
      // Chip labels seed from the BE-resolved `games[]` (gamepass-api #371);
      // unresolved ids fall through to the catalogue-lookup fallback.
      casinoGameLabels: buildCasinoGameLabelsFromTaskGames(task.games) ?? {},
      casinoAction: outcomeRuleToCasinoAction(
        firstRuleValue(task.rules, TASK_RULE_FIELD.OUTCOME),
      ),
      casinoMinBet:
        typeof task.min_bet_amount === "number"
          ? String(task.min_bet_amount)
          : "",
      wagerCurrency,
    };
  }

  if (taskType === "LOGIN_STREAK") {
    const hour =
      parseUtcHour(task.streak_reset_hour) ??
      parseUtcHour(campaignStreakResetHour);
    return {
      ...base,
      streakResetHour: hour !== undefined ? String(hour) : "",
    };
  }

  return base;
};

/** Sort challenge groups by their `sort_order` (ascending) so the Details view
 *  matches the authored order; records missing the field sink to the end. */
const compareBySortOrder = (
  a: ChallengeApiRecord,
  b: ChallengeApiRecord,
): number => {
  const toOrder = (value?: number): number =>
    typeof value === "number" ? value : Number.POSITIVE_INFINITY;
  return toOrder(a.sort_order) - toOrder(b.sort_order);
};

/** Map a campaign's challenge groups + tasks into the wizard draft list the
 *  Details page renders. */
export const mapCampaignChallengesToMonthlyDrafts = (
  challenges: ReadonlyArray<ChallengeApiRecord>,
  tasks: ReadonlyArray<CampaignTaskListRecord>,
  streakResetHour?: number | string | null,
): MonthlyChallengeDraft[] =>
  [...challenges]
    .sort(compareBySortOrder)
    .map((challenge) =>
      mapChallengeToDraft(challenge, tasks, streakResetHour),
    );

/** Campaign statuses that keep the Details page in the in-place editor. Every
 *  other status (Active, Completed, Cancelled) renders read-only. */
const EDITABLE_CAMPAIGN_STATUSES: ReadonlySet<string> = new Set([
  CAMPAIGN_API_STATUS.DRAFT,
  CAMPAIGN_API_STATUS.SCHEDULED,
]);

/**
 * Compose the full `MonthlyDetailsData` the Details page renders from a live
 * campaign record + its challenge groups + tasks. General is derived from the
 * shared card mapper (`mapApiCampaignToSeasonData`) so the header matches the
 * listing card; `mode` keys off the exact status (only Draft / Scheduled are
 * editable). Active surfaces the destructive End affordance.
 */
export const mapApiCampaignToMonthlyDetails = (
  campaign: CampaignApiRecord,
  challenges: ReadonlyArray<ChallengeApiRecord> = [],
  tasks: ReadonlyArray<CampaignTaskListRecord> = [],
): MonthlyDetailsData => {
  const ui = mapApiCampaignToSeasonData(campaign);
  const editable = EDITABLE_CAMPAIGN_STATUSES.has(campaign.status);
  const isActive = campaign.status === CAMPAIGN_API_STATUS.ACTIVE;
  const isScheduled = campaign.status === CAMPAIGN_API_STATUS.SCHEDULED;
  const isDraft = campaign.status === CAMPAIGN_API_STATUS.DRAFT;

  return {
    id: ui.id,
    mode: editable ? "editable" : "locked",
    general: {
      name: ui.name,
      // `buildCampaignStatusBadge` returns undefined only for an unrecognized
      // BE status. Fall back to the raw status string (not the campaign name)
      // so the pill shows the actual value and the mismatch is visible.
      status: ui.status ?? { label: campaign.status, tone: "draft" },
      countdown: ui.countdown,
      // The Details header shows the full date+time window (e.g.
      // "August 1, 2026 (12:00am) - …"), matching Season Details. Falls back to
      // the card's compact `MMM D` range when the window is missing/unparseable.
      dateRange:
        formatMonthlyFullDateTimeRange(
          campaign.start_date,
          campaign.end_date,
        ) ?? ui.dateRange,
      ...(isActive ? { endEnabled: true } : {}),
      // Only a SCHEDULED campaign can be pulled back to draft — a plain draft
      // has nothing to unschedule, and Active/Completed are past that point.
      ...(isScheduled ? { unscheduleEnabled: true } : {}),
      // A DRAFT can be scheduled (write its window + transition out of draft)
      // via the General edit affordance; other statuses already have a window.
      ...(isDraft ? { scheduleEnabled: true } : {}),
    },
    challenges: mapCampaignChallengesToMonthlyDrafts(
      challenges,
      tasks,
      campaign.streak_reset_hour,
    ),
  };
};
